import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthForm from "@/components/auth/AuthForm";
import PasswordResetForm from "@/components/auth/PasswordResetForm";
import PasswordUpdateForm from "@/components/auth/PasswordUpdateForm";

const Auth = () => {
  const [showResetForm, setShowResetForm] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // State carried from /auth/confirm — when the user follows a password-reset
  // link, AuthConfirm hands the Better Auth reset token here so PasswordUpdateForm
  // can call resetPassword({ token, newPassword }) without an active session.
  const showPasswordUpdate = location.state?.showPasswordUpdate || false;
  const fromTokenVerification = location.state?.fromTokenVerification || false;
  const resetToken = location.state?.resetToken as string | undefined;

  // Backwards-compat URL parameters; new flow uses /auth/confirm + state
  // but old emails or shared links may still hit ?reset=true.
  const isReset = searchParams.get("reset") === "true" ||
                  searchParams.get("type") === "recovery";

  const redirectParam = searchParams.get("redirect");

  useEffect(() => {
    if (user && !isReset && !showResetForm && !showPasswordUpdate) {
      navigate(redirectParam ?? "/", { replace: true });
    }
  }, [user, isReset, showResetForm, showPasswordUpdate, navigate, redirectParam]);

  if (showPasswordUpdate) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <PasswordUpdateForm
            resetToken={resetToken}
            onCancel={() => navigate("/auth")}
            onSuccess={() => {
              if (fromTokenVerification) {
                navigate("/auth");
                setTimeout(() => window.location.reload(), 100);
              } else {
                navigate("/");
              }
            }}
          />
        </div>
      </div>
    );
  }

  if (isReset || showResetForm) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <PasswordResetForm
            onCancel={() => {
              setShowResetForm(false);
              if (isReset) {
                navigate("/auth");
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-md mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
        <AuthForm onResetClick={() => setShowResetForm(true)} />
      </div>
    </div>
  );
};

export default Auth;
